import React, { useEffect, useState, useMemo } from "react";
import axios from "axios";
import {
  Table,
  Header,
  TextFilter,
  Pagination,
  CollectionPreferences,
  Badge,
  SpaceBetween,
  Button,
  ContentLayout
} from "@cloudscape-design/components";
import { useCollection } from "@cloudscape-design/collection-hooks";
import { MOCK_IMPORTS } from "../../mocks/mockImports"; // ← create this file locally
import "@cloudscape-design/global-styles/index.css";

/* -------------------------------------------------------------
 *  Runtime environment flags
 *  We only serve mock data when running **locally** (localhost/127.0.0.1).
 *  Remote dev/staging/back‑office still hit the real API.
 * -------------------------------------------------------------*/
const stage        = process.env.REACT_APP_STAGE;       // "dev", "staging", "prod", …
const hostname     = window.location.hostname;
const isLocalHost  = hostname === "localhost" || hostname === "127.0.0.1";
const isLocalMock  = isLocalHost && stage !== "prod";   // mock only on local machine

/* -------------------------------------------------------------
 *  Cache helpers (skipped when mocking)
 * -------------------------------------------------------------*/
const CACHE_KEY    = "imports_cache_v1";
const CACHE_TTL_MS = 1; // temporarily changing to 1 ms so that cache doesn't affect experience

// Function to format date from YYYY-MM-DD to DD/MM/YYYY without timezone conversion
function formatStaticDate(dateStr) {
  if (!dateStr) return "";
  const [year, month, day] = dateStr.split('-');
  return `${day}/${month}/${year}`;
}

function getCachedRows() {
  try {
    const cached = JSON.parse(localStorage.getItem(CACHE_KEY) || "null");
    if (cached && Date.now() - cached.timestamp < CACHE_TTL_MS) {
      return cached.rows;
    }
  } catch {}
  return [];
}

function setCachedRows(rows) {
  if (isLocalMock) return; // never persist mocks
  try {
    localStorage.setItem(
      CACHE_KEY,
      JSON.stringify({ timestamp: Date.now(), rows }),
    );
  } catch {}
}

/* -------------------------------------------------------------
 *  Column definitions
 * -------------------------------------------------------------*/
export const COLUMN_DEFINITIONS = [
  { id: "referencia", header: "Referência", cell: i => i.referencia, sortingField: "referencia"  },
  { id: "di",         header: "DI",         cell: i => i.di         , sortingField: "di"         },
  { id: "bu",         header: "BU",         cell: i => i.bu         , sortingField: "bu"         },
  { id: "poso",       header: "PO/SO",      cell: i => i.poso       , sortingField: "poso"       },
  { id: "regime",     header: "Regime",     cell: i => i.regime     , sortingField: "regime"     },
  { id: "modal",      header: "MODAL",      cell: i => i.modal      , sortingField: "modal"      },
  { id: "etd", header: "ETD", cell: i => formatStaticDate(i.etd), sortingField: "etd" },
  { id: "eta", header: "ETA", cell: i => formatStaticDate(i.eta), sortingField: "eta" },
  { id: "descricao",  header: "Descrição",  cell: i => i.descricao  , minWidth    : 280          },
  {
    id: "status",
    header: "Status",
    cell: i =>
      i.active ? (
        <Badge color="green" iconName="status-positive">Ativo</Badge>
      ) : (
        <Badge color="grey" iconName="status-inactive">Arquivado</Badge>
      ),
  },
];

/* -------------------------------------------------------------
 *  Empty / No‑match helpers
 * -------------------------------------------------------------*/
const TableEmptyState = () => (
  <SpaceBetween size="l" direction="vertical">
    <strong>Nenhum processo encontrado</strong>
    <div>Os registros aparecerão aqui assim que forem criados.</div>
  </SpaceBetween>
);

/* -------------------------------------------------------------
 *  Main content component – rendered inside <Outlet/>
 * -------------------------------------------------------------*/
export default function ProcessosPage() {
  /* choose seed data */
  const [rows, setRows]   = useState(isLocalMock ? MOCK_IMPORTS : getCachedRows);
  const [loading, setLoading] = useState(!isLocalMock && rows.length === 0);

  /* -------------------------  Fetch data  ---------------------- */
  useEffect(() => {
    if (isLocalMock) return; // skip network when using mocks

    let isMounted   = true;
    const controller = new AbortController();

    const accessToken = localStorage.getItem("access_token");

    // if cache fresh, skip fetch
    const cachedTs = JSON.parse(localStorage.getItem(CACHE_KEY) || "null")?.timestamp || 0;
    if (rows.length && Date.now() - cachedTs < CACHE_TTL_MS) {
      return () => {};
    }

    (async () => {
      if (isMounted) setLoading(true);
      try {
        const { data } = await axios.get(
          "https://jbc540hz12.execute-api.us-east-2.amazonaws.com/imports",
          { headers: { Authorization: accessToken }, signal: controller.signal },
        );
        if (stage === "dev") console.log("imports", data);
        if (isMounted) {
          setRows(data);
          setCachedRows(data);
        }
      } catch (err) {
        if (!axios.isCancel(err)) {
          console.error("Failed to fetch imports", err);
          if (isMounted) setRows([]);
        }
      } finally {
        if (isMounted) setLoading(false);
      }
    })();

    return () => {
      isMounted = false;
      controller.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* -----------------  Cloudscape collection hooks  -------------*/
  const {
    items,
    collectionProps,
    filterProps,
    paginationProps,
    preferencesProps,
    actions,
  } = useCollection(rows, {
    filtering: { empty: <TableEmptyState />, noMatch: null },
    pagination: { pageSize: 20 },
    sorting:   { defaultState: { sortingColumn: COLUMN_DEFINITIONS[7], isDescending: true } },
    selection: {},
  });

  const noMatchElement = useMemo(() => (
    <SpaceBetween size="s" direction="vertical">
      <strong>Nenhum resultado</strong>
      <Button onClick={() => actions.setFiltering("")}>Limpar filtro</Button>
    </SpaceBetween>
  ), [actions]);

  if (collectionProps?.filtering) collectionProps.filtering.noMatch = noMatchElement;

  /* ---------------------------  UI  ---------------------------- */
  return (
    <ContentLayout header={<Header variant="h1">Painel de Processos</Header>}>
      <Table
        {...collectionProps}
        stickyHeader
        stickyColumns={{ first: 1 }}
        trackBy="referencia"
        loading={loading}
        loadingText="Carregando processos"
        resizableColumns
        variant="borderless"
        columnDefinitions={COLUMN_DEFINITIONS}
        items={items}
        filter={<TextFilter {...filterProps} />}
        pagination={<Pagination {...paginationProps} />}
        preferences={<CollectionPreferences {...preferencesProps} />}
      />
    </ContentLayout>
  );
}
